import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import ERPProjectRatings from '../components/admin/ERPProjectRatings';

const HRPerformanceView = () => {
  const { getAuthHeaders } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [filterForm, setFilterForm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [forms, setForms] = useState([]);
  
  // Documentation dialog and fields
  const [openDocDialog, setOpenDocDialog] = useState(false);
  const [reviewForDoc, setReviewForDoc] = useState(null);
  const [meetingHappened, setMeetingHappened] = useState(false);
  const [managerCommitments, setManagerCommitments] = useState('');
  const [employeeCommitments, setEmployeeCommitments] = useState('');
  const [finalReviewDecision, setFinalReviewDecision] = useState('');
  const [overallFeedback, setOverallFeedback] = useState('');
  const [conductingMeeting, setConductingMeeting] = useState(false);

  useEffect(() => {
    fetchReviews();
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API_URL}/performance/forms`, {
        headers: getAuthHeaders()
      });
      setForms(response.data.forms);
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const params = {};
      if (filterForm) params.formId = filterForm;
      if (filterStatus) params.status = filterStatus;

      const response = await axios.get(`${API_URL}/performance/reviews`, {
        headers: getAuthHeaders(),
        params
      });
      setReviews(response.data.reviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to fetch performance reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [filterForm, filterStatus]);

  const handleViewReview = async (reviewId) => {
    try {
      const response = await axios.get(`${API_URL}/performance/reviews/${reviewId}`, {
        headers: getAuthHeaders()
      });
      setSelectedReview(response.data.review);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error fetching review:', error);
      toast.error('Failed to fetch performance review');
    }
  };

  const handleOpenDocumentation = async (reviewId) => {
    try {
      const response = await axios.get(`${API_URL}/performance/reviews/${reviewId}`, {
        headers: getAuthHeaders()
      });
      setReviewForDoc(response.data.review);
      setOpenDocDialog(true);
      
      // Reset documentation fields
      setMeetingHappened(false);
      setManagerCommitments('');
      setEmployeeCommitments('');
      setFinalReviewDecision('');
      setOverallFeedback('');
    } catch (error) {
      console.error('Error fetching review:', error);
      toast.error('Failed to fetch performance review');
    }
  };

  const handleConductMeeting = async () => {
    if (!meetingHappened) {
      toast.error('Please confirm that the meeting has taken place');
      return;
    }
    
    if (!managerCommitments || !employeeCommitments || !finalReviewDecision || !overallFeedback) {
      toast.error('All post-meeting fields are required');
      return;
    }

    setConductingMeeting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${reviewForDoc.reviewId}/conduct-meeting`,
        {
          managerCommitments,
          employeeCommitments,
          finalReviewDecision,
          overallFeedback
        },
        {
          headers: getAuthHeaders()
        }
      );
      toast.success('Meeting marked as conducted. Appraisal process completed!');
      setOpenDocDialog(false);
      setReviewForDoc(null);
      fetchReviews();
    } catch (error) {
      console.error('Error conducting meeting:', error);
      toast.error(error.response?.data?.message || 'Failed to mark meeting as conducted');
    } finally {
      setConductingMeeting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'manager_submitted':
        return 'info';
      case 'employee_submitted':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPerformanceRating = (score) => {
    if (score >= 4.6) {
      return {
        label: 'Exceptional Performance',
        description: 'Consistently delivers outstanding results and goes above and beyond expectations.',
        color: '#1b5e20',
        bgcolor: '#c8e6c9'
      };
    } else if (score >= 4.0) {
      return {
        label: 'Strong Performance',
        description: 'Often exceeds expectations and demonstrates a high level of skill and ownership.',
        color: '#2e7d32',
        bgcolor: '#a5d6a7'
      };
    } else if (score >= 3.0) {
      return {
        label: 'Solid Performance',
        description: 'Meets expectations well and performs the role reliably and consistently.',
        color: '#1565c0',
        bgcolor: '#90caf9'
      };
    } else if (score >= 2.0) {
      return {
        label: 'Developing Performance',
        description: 'On the right track, with some areas that need improvement and further development.',
        color: '#ef6c00',
        bgcolor: '#ffcc80'
      };
    } else {
      return {
        label: 'Growth Needed',
        description: 'Requires focused improvement and additional support to meet role expectations.',
        color: '#c62828',
        bgcolor: '#ef9a9a'
      };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Performance Reviews - HR View</Typography>

      <Box display="flex" gap={2} mb={3}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Form</InputLabel>
          <Select
            value={filterForm}
            onChange={(e) => setFilterForm(e.target.value)}
            label="Filter by Form"
          >
            <MenuItem value="">All Forms</MenuItem>
            {forms.map((form) => (
              <MenuItem key={form._id} value={form._id}>
                {form.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            label="Filter by Status"
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="employee_submitted">Employee Submitted</MenuItem>
            <MenuItem value="manager_submitted">Manager Submitted</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Form</TableCell>
              <TableCell>Review Cycle</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Employee Submitted</TableCell>
              <TableCell>Manager Submitted</TableCell>
              <TableCell>Final Score</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Alert severity="info">No performance reviews found.</Alert>
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((review) => (
                <TableRow key={review._id}>
                  <TableCell>
                    {review.employeeId?.firstName} {review.employeeId?.lastName}
                    <br />
                    <Typography variant="caption" color="textSecondary">
                      {review.employeeId?.email}
                    </Typography>
                  </TableCell>
                  <TableCell>{review.formId?.title}</TableCell>
                  <TableCell>
                    {review.formId?.reviewCycle} {review.formId?.reviewYear}
                  </TableCell>
                  <TableCell>
                    {review.managerId?.firstName} {review.managerId?.lastName}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={review.status}
                      color={getStatusColor(review.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {review.employeeSubmitted ? (
                      <Chip label="Yes" color="success" size="small" />
                    ) : (
                      <Chip label="No" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {review.managerSubmitted ? (
                      <Chip label="Yes" color="success" size="small" />
                    ) : (
                      <Chip label="No" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {review.calculatedScores?.finalScore ? (
                      <Typography variant="body2" fontWeight="bold">
                        {review.calculatedScores.finalScore.toFixed(2)}/5.00
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewReview(review._id)}
                      >
                        View Details
                      </Button>
                      {review.status === 'meeting_accepted' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => handleOpenDocumentation(review._id)}
                        >
                          Complete Documentation
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Review Details Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Performance Review Details
        </DialogTitle>
        <DialogContent>
          {selectedReview && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Employee</Typography>
                  <Typography variant="body1">
                    {selectedReview.employee?.firstName} {selectedReview.employee?.lastName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedReview.employee?.email}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Manager</Typography>
                  <Typography variant="body1">
                    {selectedReview.manager?.firstName} {selectedReview.manager?.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Form</Typography>
                  <Typography variant="body1">{selectedReview.form?.title}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip
                    label={selectedReview.status}
                    color={getStatusColor(selectedReview.status)}
                    size="small"
                  />
                </Grid>

                {/* ERP Project Ratings - Compact View */}
                {selectedReview.employee?.email && (
                  <Grid item xs={12}>
                    <ERPProjectRatings 
                      employeeEmail={selectedReview.employee.email} 
                      compact={true} 
                    />
                  </Grid>
                )}

                {selectedReview.calculatedScores?.finalScore && (() => {
                  const rating = getPerformanceRating(selectedReview.calculatedScores.finalScore);
                  return (
                    <Grid item xs={12}>
                      <Card sx={{ bgcolor: rating.bgcolor, border: '2px solid', borderColor: rating.color }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h5" sx={{ color: rating.color, fontWeight: 'bold' }}>
                              Final Performance Score: {selectedReview.calculatedScores.finalScore.toFixed(2)}/5.00
                            </Typography>
                            <Chip 
                              label={rating.label} 
                              sx={{ 
                                bgcolor: rating.color, 
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.9rem',
                                px: 1
                              }} 
                            />
                          </Box>
                          <Typography variant="body1" sx={{ color: rating.color, fontStyle: 'italic' }}>
                            {rating.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })()}
              </Grid>

              {/* Appraisal Meeting Information */}
              {selectedReview.appraisalMeeting && (
                <Card sx={{ mb: 3, bgcolor: selectedReview.status === 'completed' ? 'success.light' : 'info.light' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: selectedReview.status === 'completed' ? 'success.dark' : 'info.dark' }}>
                      {selectedReview.status === 'completed' ? '✅ Appraisal Meeting Details' : '📅 Appraisal Meeting Status'}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                        <Chip
                          label={
                            selectedReview.status === 'completed' ? 'Completed' :
                            selectedReview.status === 'meeting_accepted' ? 'Meeting Accepted' :
                            selectedReview.status === 'meeting_scheduled' ? 'Awaiting Employee Response' :
                            selectedReview.status === 'meeting_scheduling_pending' ? 'Manager Scheduling Pending' :
                            selectedReview.status === 'reschedule_required' ? 'Reschedule Required' :
                            'In Progress'
                          }
                          color={
                            selectedReview.status === 'completed' ? 'success' :
                            selectedReview.status === 'meeting_accepted' ? 'info' :
                            selectedReview.status === 'meeting_scheduled' ? 'warning' :
                            selectedReview.status === 'reschedule_required' ? 'error' :
                            'default'
                          }
                        />
                      </Grid>
                      
                      {selectedReview.appraisalMeeting.proposedDate && (
                        <>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color="textSecondary">Meeting Date</Typography>
                            <Typography variant="body1">
                              {new Date(selectedReview.appraisalMeeting.proposedDate).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color="textSecondary">Meeting Time</Typography>
                            <Typography variant="body1">{selectedReview.appraisalMeeting.proposedTime}</Typography>
                          </Grid>
                        </>
                      )}
                      
                      {selectedReview.appraisalMeeting.employeeResponse?.status === 'declined' && (
                        <Grid item xs={12}>
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Typography variant="body2" gutterBottom>
                              <strong>Employee Declined Meeting</strong>
                            </Typography>
                            <Typography variant="body2">
                              <strong>Reason:</strong> {selectedReview.appraisalMeeting.employeeResponse.declineReason}
                            </Typography>
                            {selectedReview.appraisalMeeting.employeeResponse.alternateDate && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Suggested Alternate:</strong>{' '}
                                {new Date(selectedReview.appraisalMeeting.employeeResponse.alternateDate).toLocaleDateString()}{' '}
                                at {selectedReview.appraisalMeeting.employeeResponse.alternateTime}
                              </Typography>
                            )}
                          </Alert>
                        </Grid>
                      )}
                      
                      {selectedReview.appraisalMeeting.meetingConducted && (
                        <>
                          <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom color="success.dark">
                              Meeting Documentation
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                              Manager Commitments
                            </Typography>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="body2">
                                {selectedReview.appraisalMeeting.managerCommitments}
                              </Typography>
                            </Paper>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                              Employee Commitments
                            </Typography>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="body2">
                                {selectedReview.appraisalMeeting.employeeCommitments}
                              </Typography>
                            </Paper>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                              Final Review Decision
                            </Typography>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                              <Typography variant="body2">
                                {selectedReview.appraisalMeeting.finalReviewDecision}
                              </Typography>
                            </Paper>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                              Overall Feedback / Summary
                            </Typography>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="body2">
                                {selectedReview.appraisalMeeting.overallFeedback}
                              </Typography>
                            </Paper>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary">Meeting Conducted On</Typography>
                            <Typography variant="body1">
                              {new Date(selectedReview.appraisalMeeting.conductedAt).toLocaleDateString()}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {selectedReview.form?.kras && selectedReview.form.kras.map((kra, kraIndex) => {
                // Get KRA comments from first KPI's comment field
                const employeeKraComment = selectedReview.employeeRatings?.find(
                  r => r.kraIndex === kraIndex && r.kpiIndex === 0
                )?.comment;
                const managerKraComment = selectedReview.managerRatings?.find(
                  r => r.kraIndex === kraIndex && r.kpiIndex === 0
                )?.comment;

                return (
                  <Card key={kraIndex} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {kra.name} ({kra.weightage}%)
                      </Typography>

                      {kra.kpis.map((kpi, kpiIndex) => {
                        const er = selectedReview.employeeRatings?.find(
                          r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
                        );
                        const mr = selectedReview.managerRatings?.find(
                          r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
                        );
                        const ftr = selectedReview.calculatedScores?.ftrScores?.find(
                          s => s.kraIndex === kraIndex && s.kpiIndex === kpiIndex
                        );

                        return (
                          <Box key={kpiIndex} sx={{ mb: 2, p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {kpi.name}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="textSecondary">Employee Rating</Typography>
                                <Typography variant="body2">{er?.rating || 'N/A'}/5</Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="textSecondary">Manager Rating</Typography>
                                <Typography variant="body2">{mr?.rating || 'N/A'}/5</Typography>
                              </Grid>
                              {ftr && (
                                <Grid item xs={4}>
                                  <Typography variant="caption" color="textSecondary">Final Task Rating (FTR)</Typography>
                                  <Typography variant="body2" fontWeight="bold">{ftr.ftr.toFixed(2)}</Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        );
                      })}
                      
                      {/* KRA Comments Section - After KPIs */}
                      {(employeeKraComment || managerKraComment) && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                            KRA Comments
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Employee Comment:
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {employeeKraComment || 'No comment provided'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Manager Comment:
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {managerKraComment || 'No comment provided'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              {selectedReview.employeeOverallComment && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Employee Comments</Typography>
                    <Typography variant="body2">{selectedReview.employeeOverallComment}</Typography>
                  </CardContent>
                </Card>
              )}

              {selectedReview.managerOverallComment && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Manager Comments</Typography>
                    <Typography variant="body2">{selectedReview.managerOverallComment}</Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Complete Documentation Dialog */}
      <Dialog open={openDocDialog} onClose={() => setOpenDocDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Complete Appraisal Documentation
        </DialogTitle>
        <DialogContent>
          {reviewForDoc && (
            <Box sx={{ pt: 2 }}>
              {/* Review Summary */}
              <Card sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Employee</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {reviewForDoc.employee?.firstName} {reviewForDoc.employee?.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Manager</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {reviewForDoc.manager?.firstName} {reviewForDoc.manager?.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Form</Typography>
                      <Typography variant="body1">{reviewForDoc.form?.title}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Final Score</Typography>
                      <Typography variant="h6" color="primary">
                        {reviewForDoc.calculatedScores?.finalScore?.toFixed(2) || 'N/A'}/5.00
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Meeting Confirmation Checkbox */}
              <Card sx={{ mb: 3, border: '2px solid', borderColor: 'grey.400' }}>
                <CardContent>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={meetingHappened}
                        onChange={(e) => setMeetingHappened(e.target.checked)}
                        color="primary"
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                      />
                    }
                    label={
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Has the appraisal meeting been conducted? *
                      </Typography>
                    }
                  />
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 4.5 }}>
                    Please confirm that the face-to-face or virtual appraisal meeting has taken place before completing this documentation.
                  </Typography>
                </CardContent>
              </Card>

              <Paper elevation={0} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                <Typography variant="body2" color="textSecondary">
                  Please complete all fields below to finalize the appraisal process.
                </Typography>
              </Paper>
              
              {/* Documentation Form */}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Manager Commitments *"
                    value={managerCommitments}
                    onChange={(e) => setManagerCommitments(e.target.value)}
                    placeholder="What support, resources, or actions will the manager provide to the employee?"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Employee Commitments *"
                    value={employeeCommitments}
                    onChange={(e) => setEmployeeCommitments(e.target.value)}
                    placeholder="What commitments did the employee make for improvement and development?"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Final Review Decision *"
                    value={finalReviewDecision}
                    onChange={(e) => setFinalReviewDecision(e.target.value)}
                    placeholder="Summary of the final review decision and any action items"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Overall Feedback / Summary *"
                    value={overallFeedback}
                    onChange={(e) => setOverallFeedback(e.target.value)}
                    placeholder="Comprehensive summary of the appraisal discussion"
                    required
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDocDialog(false)} disabled={conductingMeeting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConductMeeting}
            disabled={conductingMeeting || !meetingHappened || !managerCommitments || !employeeCommitments || !finalReviewDecision || !overallFeedback}
          >
            {conductingMeeting ? <CircularProgress size={24} /> : 'Complete Appraisal'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HRPerformanceView;

